<template>
  <BonusCard
    :id="freebet.id"
    :progress
    :steps-progress="stepsProgress"
    :timer="bonusTimer"
    type="freebet"
    :paused="rollingStatus === 'paused'"
    data-t="freebet-card"
  >
    <template #subtitle>
      <div class="amount-container">
        <div class="subtitle-block">
          <StIcon
            v-if="isEnabledStepsRolling"
            class="lock-icon"
            name="lock-solid"
            :size="16"
          />
          <span data-t="freebet-amount">{{ freebetAmount }}</span>
          <StIcon :name="freebet.currencyIcon" :size="16" />
        </div>
        <div v-if="isEnabledStepsRolling" class="steps-amount">
          <div
            v-if="unlockedAmount"
            class="steps-amount-info"
            data-t="unlocked-amount"
          >
            {{ t('bonuses.unlockedAmount') }}
            {{ format(unlockedAmount) }}
            <StIcon :name="freebet.currencyIcon" :size="14" />
          </div>
          <div v-else class="steps-amount-info" data-t="until-next-claim">
            {{ t('bonuses.untilNextClaim') }}
            {{ format(untilNextClaimAmount) }}
            <StIcon :name="freebet.currencyIcon" :size="14" />
          </div>
        </div>
      </div>
    </template>

    <BonusCardBorderedContent
      v-if="status === 'new'"
      class="new-content"
      data-t="new-content"
    >
      <BonusCardTerms :list="filteredList" />
    </BonusCardBorderedContent>

    <BonusCardBorderedContent
      v-if="status === 'process'"
      class="proccess-content"
      data-t="proccess-content"
    >
      <StSpinner :size="20" class="button-loader" />
      <span class="process-subtitle">{{ t('bonuses.betDone') }}</span>
    </BonusCardBorderedContent>

    <template v-if="status === 'rolling'" #progress-text>
      <div class="rolling-data">
        <span class="wager">
          {{ t('bonuses.wager', { multiplier: wager }) }}
        </span>
        <span class="current-rolling" data-t="current-rolling">
          {{ minMaxRollingTextWithoutCurrency }}
          <StIcon :name="freebet.currencyIcon" :size="16" />
        </span>
      </div>
    </template>

    <template #actions>
      <div class="actions-wrapper">
        <StButton
          v-if="freebetButton"
          class="button"
          v-bind="freebetButton"
          @click="freebetButton.click"
        />
      </div>
    </template>
    <LottieClaimAnimation ref="claimAnimation" :is-big="isMobile" />
  </BonusCard>
</template>

<script setup lang="ts">
import { delay } from '@st/utils'
import LottieClaimAnimation from '@st/ui/components/LottieClaimAnimation/LottieClaimAnimation.client.vue'
import BonusCard from '../../BonusCard/BonusCard.vue'
import BonusCardTerms from '../../BonusCard/parts/BonusCardTerms.vue'
import BonusCardBorderedContent from '../../BonusCard/parts/BonusCardBorderedContent.vue'
import type { Freebet } from '../../../types'
import { useFreebet } from '../../../composables/useFreebet'
import { useBonusStepRolling } from '../../../composables/useBonusStepRolling'

const animation =
  useTemplateRef<InstanceType<typeof LottieClaimAnimation>>('claimAnimation')

const props = defineProps<{
  freebet: Freebet
}>()
const { t } = useI18n()
const { freebet } = toRefs(props)
const { isMobile } = usePlatform()
const {
  progress,
  currentExpiredDate,
  status,
  timerTitle,
  filteredList,
  wager,
  rollingStatus,
  minMaxRollingTextWithoutCurrency,
  setCurrentRolling,
  fetchFreebets,
} = useFreebet(freebet)

const { format } = useCryptoFormatter()
const freebetAmount = computed(() =>
  status.value === 'rolling'
    ? format(freebet.value.payoff ?? 0)
    : format(freebet.value.amount),
)

const bonusTimer = computed(() => {
  if (status.value === 'process') return null

  return {
    expiresAt: currentExpiredDate.value,
    title: timerTitle.value,
  }
})

const isVisibleRollingButton = computed(
  () => rollingStatus.value && ['new', 'paused'].includes(rollingStatus.value),
)

const stepRollingData = computed(() => ({
  stepRollingData: freebet.value.rollingSteps ?? [],
  rollingData: freebet.value.rolling,
  bonusAmount: freebet.value.payoff ?? '0',
}))
const {
  unlockedAmount,
  untilNextClaimAmount,
  canPartClaim,
  partClaim,
  partClaimStatus,
  isEnabledStepsRolling,
  stepsProgress,
} = useBonusStepRolling(stepRollingData)

async function handlePartClaim() {
  await partClaim()
  if (partClaimStatus.value === 'success') {
    await animation.value?.runClaimAnimation()
    await delay(300)
    await fetchFreebets()
  }
}

const freebetButton = computed(() => {
  if (status.value === 'new') {
    return {
      label: t('bonuses.makeBet'),
      icon: undefined,
      type: 'primary' as const,
      to: '/sport',
    }
  }

  if (status.value === 'process') {
    return {
      label: t('bonuses.betHistory'),
      icon: 'bet' as const,
      type: 'ghost' as const,
      to: '/personal/bets?tab=sport',
      'is-left-icon': true,
    }
  }

  if (rollingStatus.value) {
    if (canPartClaim.value || rollingStatus.value === 'rolled') {
      return {
        label: t('bonuses.takeBonus'),
        icon: undefined,
        type: 'primary' as const,
        loading: partClaimStatus.value === 'pending',
        disabled: ['success', 'error'].includes(partClaimStatus.value),
        isPulsing: true,
        click: handlePartClaim,
      }
    }

    if (isVisibleRollingButton.value) {
      return {
        label:
          rollingStatus.value === 'paused'
            ? t('bonuses.resumeRolling')
            : t('bonuses.startRolling'),
        icon: undefined,
        type: 'secondary' as const,
        click: setCurrentRolling,
      }
    }

    return {
      label: t('bonuses.goToGame'),
      icon: 'play-solid' as const,
      type: 'primary' as const,
      to: '/sport',
      'is-left-icon': true,
    }
  }

  return null
})
</script>

<style scoped>
.actions-wrapper {
  position: relative;
  display: flex;
  flex: 1 1 50%;
  min-width: 0;
}

.subtitle-block {
  display: flex;
  gap: var(--spacing-025);
  align-items: center;
}

.button {
  flex: 1 1 0;
  min-width: 0;
}

.rolling-data {
  display: flex;
  align-items: center;
  justify-content: space-between;

  font: var(--mobile-caption-1-regular);
  color: var(--text-primary);
}

.current-rolling {
  display: flex;
  gap: var(--spacing-025);
  align-items: center;
}

.amount-container {
  display: flex;
  gap: var(--spacing-050);
  align-items: center;
}

.steps-amount {
  margin-left: auto;
  font: var(--mobile-caption-1-regular);
  color: var(--text-primary);
}

.steps-amount-info {
  overflow: hidden;
  display: flex;
  gap: var(--spacing-025);
  align-items: center;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.lock-icon {
  color: var(--icon-secondary);
}
</style>
